import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { cx } from "shared/utils";
import { Show } from "shared/components";
import { AvailabilityOverviewTeamMember } from "api/scheduling/types";
import {
  AVAILABLE_FILL_CLASS,
  HOUR_ROW_PX,
  MEMBER_COLUMN_PX,
  UNAVAILABLE_REASON_COPY
} from "./constants";
import {
  buildDayRegions,
  findDay,
  formatHourLabel,
  getShowingDisplay,
  formatTime,
  minutesToPx,
  showingGeometry
} from "./helpers";
import { UnavailableRegion } from "./UnavailableRegion";

interface DayTimelineProps {
  teamMembers: AvailabilityOverviewTeamMember[];
  selectedDate: string;
  timezone: string;
  bandStartHour: number;
  bandEndHour: number;
}

// Height of the sticky member-name header row (Figma: 48px), also the top
// offset of the hour axis so its labels align with the gridlines.
const HEADER_PX = 48;

// Upper bound on the scrollable grid height, per the Figma spec (the calendar
// scrolls internally rather than growing the whole page — QA prefers a
// Slack-like body scroll over a full-page scroll). On shorter viewports the
// grid shrinks below this so the whole card still fits on screen and only the
// grid — not the page — scrolls (avoids the double scroll).
const GRID_MAX_HEIGHT_PX = 600;

// Never collapse the grid below this, so it stays usable on very short screens.
const GRID_MIN_HEIGHT_PX = 260;

// Breathing room kept below the grid inside the scroll container, on top of
// the container's own bottom padding (subtracted separately).
const GRID_BOTTOM_GAP_PX = 12;

// A showing card shorter than this can only fit a single line, so the time
// range is dropped to avoid clipped text.
const CARD_COMPACT_PX = 40;

// Nearest ancestor that scrolls vertically — the grid is capped to this
// element's visible height so it never spills past the fold and forces a second
// (page-level) scrollbar on top of its own internal one.
const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let el = node?.parentElement ?? null;
  while (el) {
    const overflowY = getComputedStyle(el).overflowY;
    if (
      overflowY === "auto" ||
      overflowY === "scroll" ||
      overflowY === "overlay"
    )
      return el;
    el = el.parentElement;
  }
  return null;
};

const reasonCopy = (
  reason: keyof typeof UNAVAILABLE_REASON_COPY | null,
  memberName: string
): string | null => {
  if (!reason) return null;
  const copy = UNAVAILABLE_REASON_COPY[reason];
  if (!copy) return null;
  return copy.replace("{name}", memberName);
};

export const DayTimeline = ({
  teamMembers,
  selectedDate,
  timezone,
  bandStartHour,
  bandEndHour
}: DayTimelineProps) => {
  const hourLabels = Array.from(
    { length: bandEndHour - bandStartHour + 1 },
    (_, i) => bandStartHour + i
  );
  const timelineHeight = (bandEndHour - bandStartHour) * HOUR_ROW_PX;

  // Cap the grid to the visible height of its scroll container so the card
  // always fits on screen and only the grid scrolls internally — a fixed height
  // taller than the viewport forced a second, page-level scrollbar. Recomputed
  // on mount, on data change, and on resize.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState(GRID_MAX_HEIGHT_PX);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const recompute = () => {
      const parent = getScrollParent(el);
      if (!parent) {
        setMaxHeight(GRID_MAX_HEIGHT_PX);
        return;
      }
      const offsetInParent =
        el.getBoundingClientRect().top - parent.getBoundingClientRect().top;
      // clientHeight spans the content box + padding; the grid card cannot sit
      // inside the container's bottom padding, so exclude it too.
      const paddingBottom =
        parseFloat(getComputedStyle(parent).paddingBottom) || 0;
      const available =
        parent.clientHeight -
        offsetInParent -
        paddingBottom -
        GRID_BOTTOM_GAP_PX;
      setMaxHeight(
        Math.max(
          GRID_MIN_HEIGHT_PX,
          Math.min(GRID_MAX_HEIGHT_PX, Math.round(available))
        )
      );
    };

    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, [bandStartHour, bandEndHour, teamMembers.length]);

  const columns = useMemo(
    () =>
      teamMembers.map((member) => {
        const day = findDay(member, selectedDate);
        return {
          member,
          regions: buildDayRegions(day, timezone, bandStartHour, bandEndHour),
          showings: day?.showings ?? []
        };
      }),
    [teamMembers, selectedDate, timezone, bandStartHour, bandEndHour]
  );

  return (
    // The grid body scrolls INTERNALLY inside a bounded box (QA prefers a
    // Slack-like body scroll over a full-page scroll). `scroll-on-hover` keeps
    // the scrollbar hidden until the container is hovered, so it does not
    // clutter the view when idle. `isolate` keeps the internal z-indexes in this
    // component's stacking context so the sticky header does not paint over the
    // expanded sidebar (aside is z-[14]). The grid scrolls on BOTH axes: the
    // member header sticks to the top, the hour axis to the left.
    <div
      ref={scrollRef}
      className="scroll-on-hover isolate overflow-auto"
      style={{ maxHeight }}
    >
      {/* Header row — sticky to the top while the grid scrolls vertically */}
      <div className="sticky top-0 z-30 flex w-max min-w-full">
        {/* Top-left corner — sticky on both axes */}
        <div
          className="sticky left-0 z-10 w-16 shrink-0 border-b border-alpha-80 bg-white"
          style={{ height: HEADER_PX }}
        />
        {columns.map(({ member }) => (
          <div
            key={member.partner_user.id}
            className="flex shrink-0 items-center justify-center border-b border-r border-alpha-80 bg-white px-2"
            style={{ width: MEMBER_COLUMN_PX, height: HEADER_PX }}
          >
            <span className="truncate text-sm font-medium text-content-400">
              {member.partner_user.name}
            </span>
          </div>
        ))}
      </div>

      {/* Body row — hour axis (sticky left) + member grids */}
      <div className="flex w-max min-w-full">
        {/* Hour axis — sticky so the time reference survives horizontal
            scrolling through many member columns */}
        <div className="sticky left-0 z-20 w-16 shrink-0 bg-white">
          <div className="relative" style={{ height: timelineHeight }}>
            {hourLabels.map((hour) => (
              <div
                key={hour}
                className={cx(
                  "absolute right-0 flex items-center justify-end gap-1",
                  // Edge labels are aligned inward so they are not clipped by
                  // the band edges: the first sits at the top, the last (band
                  // end, e.g. "12 AM") sits above the bottom; the rest are
                  // centered on their gridline.
                  hour === bandStartHour && "translate-y-0",
                  hour === bandEndHour && "-translate-y-full",
                  hour !== bandStartHour &&
                    hour !== bandEndHour &&
                    "-translate-y-1/2"
                )}
                style={{ top: minutesToPx((hour - bandStartHour) * 60) }}
              >
                <span className="text-sm font-medium text-content-200">
                  {formatHourLabel(hour)}
                </span>
                <span aria-hidden="true" className="h-px w-4 bg-alpha-80" />
              </div>
            ))}
          </div>
        </div>

        {/* Team member columns */}
        {columns.map(({ member, regions, showings }) => (
          <div
            key={member.partner_user.id}
            className="relative shrink-0 border-r border-alpha-80"
            style={{ width: MEMBER_COLUMN_PX, height: timelineHeight }}
          >
            {/* Hour gridlines */}
            {hourLabels.slice(1, -1).map((hour) => (
              <div
                key={hour}
                className="absolute inset-x-0 border-t border-alpha-80"
                style={{ top: minutesToPx((hour - bandStartHour) * 60) }}
              />
            ))}

            {/* Availability regions: solid green for available, hatched
                with a cursor-following range/reason tooltip for unavailable */}
            {regions.map((region, index) => {
              if (region.state === "available")
                return (
                  <div
                    key={`${region.topMinutes}-${index}`}
                    className={cx("absolute inset-x-0", AVAILABLE_FILL_CLASS)}
                    style={{
                      top: minutesToPx(region.topMinutes),
                      height: minutesToPx(region.durationMinutes)
                    }}
                  />
                );

              const title = region.allDay
                ? "Unavailable all day"
                : `Unavailable ${region.startLabel} – ${region.endLabel}`;
              return (
                <UnavailableRegion
                  key={`${region.topMinutes}-${index}`}
                  top={minutesToPx(region.topMinutes)}
                  height={minutesToPx(region.durationMinutes)}
                  title={title}
                  subtitle={reasonCopy(region.reason, member.partner_user.name)}
                />
              );
            })}

            {/* Scheduled showing cards (view-only, non-interactive —
                pointer-events-none lets hover reach the region tooltip
                beneath), clipped to the band so out-of-band showings
                cannot paint over surrounding UI */}
            {showings.map((showing) => {
              const geometry = showingGeometry(
                showing.start_time,
                showing.end_time,
                timezone,
                bandStartHour,
                bandEndHour
              );
              if (!geometry) return null;

              const display = getShowingDisplay(showing.status);
              const compact = geometry.heightPx < CARD_COMPACT_PX;
              return (
                <div
                  key={showing.id}
                  className={cx(
                    "pointer-events-none absolute inset-x-1 z-10 overflow-hidden rounded-md border border-alpha-100 shadow-sm",
                    compact ? "px-1 py-0" : "p-1",
                    display.bgClass
                  )}
                  style={{
                    top: geometry.topPx,
                    height: geometry.heightPx
                  }}
                >
                  <p
                    className={cx(
                      "truncate text-[11px] font-medium leading-tight",
                      display.textClass
                    )}
                  >
                    {display.label}
                  </p>
                  {/* Time range dropped on short cards so text is never
                      clipped (QA feedback: events cut off) */}
                  <Show if={!compact}>
                    <p className="truncate text-[10px] leading-tight text-content-500">
                      {formatTime(showing.start_time, timezone)} -{" "}
                      {formatTime(showing.end_time, timezone)}
                    </p>
                  </Show>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
